import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Bold, Italic, List, Heading2, Minus, Eye, Edit3 } from 'lucide-react';
import { koffieClient } from '../lib/supabase';
import KoffieBackButton from './KoffieBackButton';

const t = {
  bg: '#f2f0eb', white: '#ffffff', text: '#0c0c0b',
  muted: '#8a8780', lines: '#dcd9d1', accent: '#c75a0a',
  sans: "'Jost', system-ui, sans-serif",
  serif: "'Playfair Display', Georgia, serif",
};

// ─── Markdown parser ──────────────────────────────────────────────────────────

function parseMarkdown(raw) {
  const lines = raw.split('\n');
  const nodes = [];
  let i = 0;

  const parseInline = (str) => {
    const parts = [];
    let s = str;
    let key = 0;
    while (s.length > 0) {
      const bold  = s.match(/^\*\*(.+?)\*\*/);
      const em    = s.match(/^\*(.+?)\*/);
      const under = s.match(/^__(.+?)__/);
      if (bold) {
        parts.push(<strong key={key++}>{bold[1]}</strong>);
        s = s.slice(bold[0].length);
      } else if (under) {
        parts.push(<u key={key++}>{under[1]}</u>);
        s = s.slice(under[0].length);
      } else if (em) {
        parts.push(<em key={key++}>{em[1]}</em>);
        s = s.slice(em[0].length);
      } else {
        const next = s.search(/\*\*|\*|__/);
        if (next === -1) { parts.push(s); break; }
        parts.push(s.slice(0, next));
        s = s.slice(next);
      }
    }
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (/^### /.test(line)) {
      nodes.push(<h3 key={i} style={{ fontFamily: t.serif, fontSize: 16, fontWeight: 700, color: t.text, margin: '20px 0 6px', letterSpacing: '-0.01em' }}>{parseInline(line.slice(4))}</h3>);
    } else if (/^## /.test(line)) {
      nodes.push(<h2 key={i} style={{ fontFamily: t.serif, fontSize: 18, fontWeight: 900, color: t.text, margin: '24px 0 8px', letterSpacing: '-0.02em' }}>{parseInline(line.slice(3))}</h2>);
    } else if (/^# /.test(line)) {
      nodes.push(<h1 key={i} style={{ fontFamily: t.serif, fontSize: 22, fontWeight: 900, color: t.text, margin: '28px 0 10px', letterSpacing: '-0.02em' }}>{parseInline(line.slice(2))}</h1>);
    } else if (/^---/.test(line)) {
      nodes.push(<hr key={i} style={{ border: 'none', borderTop: `1px solid ${t.lines}`, margin: '20px 0' }} />);
    } else if (/^- /.test(line)) {
      const items = [];
      while (i < lines.length && /^- /.test(lines[i])) {
        items.push(<li key={i}>{parseInline(lines[i].slice(2))}</li>);
        i++;
      }
      nodes.push(<ul key={`ul-${i}`} style={{ fontFamily: t.sans, fontSize: 13, color: t.text, lineHeight: 1.7, margin: '8px 0', paddingLeft: 20 }}>{items}</ul>);
      continue;
    } else if (line.trim() === '') {
      nodes.push(<div key={i} style={{ height: 8 }} />);
    } else {
      nodes.push(<p key={i} style={{ fontFamily: t.sans, fontSize: 13, color: t.text, lineHeight: 1.7, margin: '4px 0' }}>{parseInline(line)}</p>);
    }
    i++;
  }
  return nodes;
}

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`
  height: 100%; width: 100%; background: ${t.bg};
  overflow-y: auto; -webkit-overflow-scrolling: touch;
`;

const Page = styled.div`
  padding: 36px 40px 80px; max-width: 680px; margin: 0 auto;
  @media (max-width: 600px) { padding: 24px 20px 64px; }
`;

const PageTitle = styled.h1`
  font-family: ${t.serif}; font-size: clamp(20px, 3vw, 26px);
  font-weight: 900; color: ${t.text}; letter-spacing: -0.02em; margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-family: ${t.sans}; font-size: 13px; color: ${t.muted}; margin: 0 0 24px; line-height: 1.5;
`;

const EditorCard = styled.div`
  background: ${t.white}; border: 1px solid ${t.lines}; border-radius: 8px; overflow: hidden;
`;

const EditorToolbar = styled.div`
  display: flex; align-items: center; gap: 2px;
  padding: 8px 10px; border-bottom: 1px solid ${t.lines};
  background: ${t.bg};
`;

const ToolBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 5px; border: none;
  background: ${p => p.$active ? t.lines : 'none'};
  color: ${t.muted}; cursor: pointer; transition: background 0.1s;
  &:hover { background: ${t.lines}; color: ${t.text}; }
`;

const ToolSep = styled.div`
  width: 1px; height: 16px; background: ${t.lines}; margin: 0 4px;
`;

const TabRow = styled.div`
  display: flex; border-bottom: 1px solid ${t.lines};
`;

const Tab = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 14px; border: none; background: none;
  font-family: ${t.sans}; font-size: 12px; font-weight: 500;
  color: ${p => p.$active ? t.text : t.muted};
  border-bottom: 2px solid ${p => p.$active ? t.accent : 'transparent'};
  cursor: pointer; transition: color 0.15s;
  margin-bottom: -1px;
`;

const TextArea = styled.textarea`
  width: 100%; box-sizing: border-box; min-height: 360px; resize: vertical;
  padding: 16px; border: none; outline: none;
  font-family: ${t.sans}; font-size: 13px; line-height: 1.8; color: ${t.text};
  background: ${t.white};
  &::placeholder { color: ${t.muted}; opacity: 0.5; }
`;

const Preview = styled.div`
  padding: 16px; min-height: 360px;
  h1, h2, h3 { margin-top: 0; }
  & > *:first-child { margin-top: 0 !important; }
`;

const CharCount = styled.div`
  font-family: ${t.sans}; font-size: 11px; color: ${t.muted}; text-align: right; margin-top: 6px;
`;

const SubmitBtn = styled.button`
  margin-top: 16px; width: 100%; padding: 11px; border-radius: 6px; border: none;
  background: ${p => p.disabled ? t.lines : t.text};
  color: ${p => p.disabled ? t.muted : t.bg};
  font-family: ${t.sans}; font-size: 13px; font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: opacity 0.15s; &:hover:not(:disabled) { opacity: 0.85; }
`;

const Feedback = styled.div`
  margin-top: 12px; padding: 10px 14px; border-radius: 6px;
  font-family: ${t.sans}; font-size: 12px;
  background: ${p => p.$success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'};
  color: ${p => p.$success ? '#22c55e' : '#ef4444'};
  border: 1px solid ${p => p.$success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'};
`;

const HintRow = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px;
`;

const Hint = styled.span`
  font-family: ${t.sans}; font-size: 11px; color: ${t.muted};
  code { background: ${t.lines}; border-radius: 3px; padding: 1px 4px; font-size: 10px; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const TABLE = 'Legal';
const ROW_ID = 1;

export default function KoffieTerms() {
  const [content, setContent]   = useState('');
  const [original, setOriginal] = useState('');
  const [tab, setTab]           = useState('edit');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);
  const taRef = useRef(null);

  useEffect(() => {
    koffieClient.from(TABLE).select('text').eq('id', ROW_ID).single().then(({ data }) => {
      if (data?.text) { setContent(data.text); setOriginal(data.text); }
      setLoading(false);
    });
  }, []);

  const insert = useCallback((before, after = '', sample = 'tekst') => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const sel   = content.slice(start, end) || sample;
    const next  = content.slice(0, start) + before + sel + after + content.slice(end);
    setContent(next);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = start + before.length;
      ta.selectionEnd   = start + before.length + sel.length;
    }, 0);
  }, [content]);

  const TOOLS = [
    { icon: Bold,     label: 'Vet',        action: () => insert('**', '**', 'vetgedrukt') },
    { icon: Italic,   label: 'Cursief',    action: () => insert('*', '*', 'cursief') },
    null,
    { icon: Heading2, label: 'Kop',        action: () => insert('## ', '', 'Koptekst') },
    { icon: List,     label: 'Lijst',      action: () => insert('- ', '', 'item') },
    { icon: Minus,    label: 'Scheidslijn', action: () => insert('\n---\n', '') },
  ];

  const handleSave = async () => {
    if (saving || content === original) return;
    setSaving(true); setFeedback(null);
    const { error } = await koffieClient.from(TABLE).upsert({ id: ROW_ID, text: content, edited_at: new Date().toISOString() });
    if (error) {
      setFeedback({ msg: 'Opslaan mislukt.', success: false });
    } else {
      setOriginal(content);
      setFeedback({ msg: 'Opgeslagen.', success: true });
    }
    setSaving(false);
  };

  return (
    <Wrap>
      <Page>
        <KoffieBackButton to="/Koffiereparatie/settings" />
        <PageTitle>Algemene voorwaarden</PageTitle>
        <PageSub>Bewerk de tekst die op de website wordt getoond.</PageSub>

        <EditorCard>
          <TabRow>
            <Tab $active={tab === 'edit'} onClick={() => setTab('edit')}>
              <Edit3 size={12} /> Bewerken
            </Tab>
            <Tab $active={tab === 'preview'} onClick={() => setTab('preview')}>
              <Eye size={12} /> Voorbeeld
            </Tab>
          </TabRow>

          {tab === 'edit' && (
            <>
              <EditorToolbar>
                {TOOLS.map((tool, i) =>
                  tool === null
                    ? <ToolSep key={i} />
                    : <ToolBtn key={i} type="button" title={tool.label} onClick={tool.action}>
                        <tool.icon size={14} />
                      </ToolBtn>
                )}
              </EditorToolbar>
              <TextArea
                ref={taRef}
                value={loading ? '' : content}
                onChange={e => setContent(e.target.value)}
                placeholder="Voer hier de algemene voorwaarden in…"
                disabled={loading}
              />
            </>
          )}

          {tab === 'preview' && (
            <Preview>
              {content.trim() ? parseMarkdown(content) : <p style={{ fontFamily: t.sans, fontSize: 13, color: t.muted }}>Nog geen inhoud.</p>}
            </Preview>
          )}
        </EditorCard>

        {tab === 'edit' && (
          <HintRow>
            <Hint><code>**tekst**</code> vet</Hint>
            <Hint><code>*tekst*</code> cursief</Hint>
            <Hint><code>__tekst__</code> onderstreept</Hint>
            <Hint><code>## Kop</code> koptekst</Hint>
            <Hint><code>- item</code> lijst</Hint>
            <Hint><code>---</code> lijn</Hint>
          </HintRow>
        )}

        <CharCount>{content.length} tekens</CharCount>

        <SubmitBtn onClick={handleSave} disabled={content === original || saving}>
          {saving ? 'Opslaan…' : 'Opslaan'}
        </SubmitBtn>

        {feedback && <Feedback $success={feedback.success}>{feedback.msg}</Feedback>}
      </Page>
    </Wrap>
  );
}
